import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateSeatingReservationApplicationErrors,
  CreateSeatingReservationOptions,
  DeleteSeatingReservationResponse,
  GetSeatingCategorySummaryByExternalIdResponse,
  GetSeatingReservationSummaryByExternalIdResponse,
  QuerySeatingReservationsResponse,
  ReservationsQueryBuilder,
  SeatingReservation,
  SeatingReservationCreatedEnvelope,
  SeatingReservationDeletedEnvelope,
  SeatingReservationQuery,
  createSeatingReservation as universalCreateSeatingReservation,
  deleteSeatingReservation as universalDeleteSeatingReservation,
  getSeatingCategorySummaryByExternalId as universalGetSeatingCategorySummaryByExternalId,
  getSeatingReservation as universalGetSeatingReservation,
  getSeatingReservationSummaryByExternalId as universalGetSeatingReservationSummaryByExternalId,
  querySeatingReservations as universalQuerySeatingReservations,
  typedQuerySeatingReservations as universalTypedQuerySeatingReservations,
} from './seating-v1-seating-reservation-seating-reservation.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/seatings' };

export function createSeatingReservation(
  httpClient: HttpClient
): CreateSeatingReservationSignature {
  return (options?: CreateSeatingReservationOptions) =>
    universalCreateSeatingReservation(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateSeatingReservationSignature {
  /**
   * Creates a seating reservation for one or more places within a seating plan.
   * @returns Created reservation.
   */
  (options?: CreateSeatingReservationOptions): Promise<
    NonNullablePaths<
      SeatingReservation,
      `reservedPlaces` | `reservedPlaces.${number}._id`,
      4
    > & {
      __applicationErrorsType?: CreateSeatingReservationApplicationErrors;
    }
  >;
}

export function getSeatingReservation(
  httpClient: HttpClient
): GetSeatingReservationSignature {
  return (reservationId: string) =>
    universalGetSeatingReservation(
      reservationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSeatingReservationSignature {
  /**
   * Retrieves a seating reservation by ID.
   * @param - Reservation ID.
   * @returns Retrieved reservation.
   */
  (reservationId: string): Promise<
    NonNullablePaths<
      SeatingReservation,
      `reservedPlaces` | `reservedPlaces.${number}._id`,
      4
    >
  >;
}

export function querySeatingReservations(
  httpClient: HttpClient
): QuerySeatingReservationsSignature {
  return () =>
    universalQuerySeatingReservations(
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySeatingReservationsSignature {
  /**
   * Retrieves seating reservations that match specified criteria.
   */
  (): ReservationsQueryBuilder;
}

export function typedQuerySeatingReservations(
  httpClient: HttpClient
): TypedQuerySeatingReservationsSignature {
  return (query: SeatingReservationQuery) =>
    universalTypedQuerySeatingReservations(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQuerySeatingReservationsSignature {
  /** @returns Response containing matching seating reservations. */
  (query: SeatingReservationQuery): Promise<
    NonNullablePaths<QuerySeatingReservationsResponse, `reservations`, 2>
  >;
}

export function deleteSeatingReservation(
  httpClient: HttpClient
): DeleteSeatingReservationSignature {
  return (_id: string) =>
    universalDeleteSeatingReservation(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteSeatingReservationSignature {
  /**
   * Deletes a seating reservation and releases all reserved places.
   * @param - Reservation ID.
   * @returns Response after deleting a seating reservation.
   */
  (_id: string): Promise<
    NonNullablePaths<
      DeleteSeatingReservationResponse,
      `reservation.reservedPlaces` | `reservation.reservedPlaces.${number}._id`,
      5
    >
  >;
}

export function getSeatingCategorySummaryByExternalId(
  httpClient: HttpClient
): GetSeatingCategorySummaryByExternalIdSignature {
  return (externalId: string) =>
    universalGetSeatingCategorySummaryByExternalId(
      externalId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSeatingCategorySummaryByExternalIdSignature {
  /**
   * Retrieves capacity and reservation summary by seating category.
   * @param - External seating plan ID.
   * @returns Response containing capacity summary by category.
   */
  (externalId: string): Promise<
    NonNullablePaths<
      GetSeatingCategorySummaryByExternalIdResponse,
      `categories`,
      2
    >
  >;
}

export function getSeatingReservationSummaryByExternalId(
  httpClient: HttpClient
): GetSeatingReservationSummaryByExternalIdSignature {
  return (externalId: string) =>
    universalGetSeatingReservationSummaryByExternalId(
      externalId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSeatingReservationSummaryByExternalIdSignature {
  /**
   * Retrieves detailed place-level occupancy data for a seating plan.
   * @param - External seating plan ID.
   * @returns Response containing complete seating plan and occupancy data.
   */
  (externalId: string): Promise<
    NonNullablePaths<
      GetSeatingReservationSummaryByExternalIdResponse,
      | `plan.sections`
      | `plan.sections.${number}._id`
      | `plan.sections.${number}.default`
      | `plan.categories`
      | `plan.categories.${number}._id`
      | `plan.categories.${number}.title`
      | `plan.uncategorizedPlaces`
      | `plan.elementGroups`
      | `plan.elementGroups.${number}._id`
      | `places`
      | `places.${number}.placeId`
      | `places.${number}.occupied`,
      5
    >
  >;
}

export const onSeatingReservationCreated = EventDefinition(
  'wix.seating.v1.seating_reservation_created',
  true,
  (event: SeatingReservationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SeatingReservationCreatedEnvelope>();
export const onSeatingReservationDeleted = EventDefinition(
  'wix.seating.v1.seating_reservation_deleted',
  true,
  (event: SeatingReservationDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SeatingReservationDeletedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  AvailablePlace,
  BaseEventMetadata,
  CancelSeatingPlaceReservationsRequest,
  CancelSeatingPlaceReservationsResponse,
  Category,
  CategoryDetails,
  CreateSeatingReservationOptions,
  CreateSeatingReservationRequest,
  CreateSeatingReservationResponse,
  CursorPaging,
  Cursors,
  CustomTag,
  DeleteSeatingPlaceReservationRequest,
  DeleteSeatingReservationByExternalIdRequest,
  DeleteSeatingReservationByExternalIdResponse,
  DeleteSeatingReservationRequest,
  DeleteSeatingReservationResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Element,
  ElementGroup,
  ElementGroupUiProperties,
  ElementUiProperties,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  File,
  GetReservationRequest,
  GetReservationResponse,
  GetReservedPlacesRequest,
  GetReservedPlacesResponse,
  GetSeatingCategorySummaryByExternalIdRequest,
  GetSeatingCategorySummaryByExternalIdResponse,
  GetSeatingCategorySummaryRequest,
  GetSeatingCategorySummaryResponse,
  GetSeatingReservationRequest,
  GetSeatingReservationResponse,
  GetSeatingReservationSummaryByExternalIdRequest,
  GetSeatingReservationSummaryByExternalIdResponse,
  GetSeatingReservationSummaryRequest,
  GetSeatingReservationSummaryResponse,
  Icon,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ListAvailablePlacesRequest,
  ListAvailablePlacesResponse,
  ListReservedPlacesRequest,
  ListReservedPlacesResponse,
  MessageEnvelope,
  MultiRowProperties,
  Numbering,
  Page,
  Pages,
  Paging,
  PagingMetadata,
  PagingMetadataV2,
  Place,
  PlaceReservation,
  PlaceReservationDetails,
  PlaceTypeEnumType,
  Places,
  Position,
  QuerySeatingReservationRequest,
  QuerySeatingReservationResponse,
  QuerySeatingReservationsRequest,
  QuerySeatingReservationsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RegenerateSummariesRequest,
  RegenerateSummariesResponse,
  ReservationErrorDetails,
  ReservationOptions,
  ReservationsQueryBuilder,
  ReservationsQueryResult,
  ReservedPlace,
  RestoreInfo,
  RowElement,
  RowElementUiProperties,
  SeatReservation,
  SeatingPlan,
  SeatingPlanCategoriesSummaryUpdated,
  SeatingPlanUiProperties,
  SeatingReservation,
  SeatingReservationCreatedEnvelope,
  SeatingReservationDeletedEnvelope,
  SeatingReservationQuerySpec,
  SeatingReservationsSummary,
  Section,
  Sequencing,
  ShapeTypeEnumType,
  SortOrder,
  Sorting,
  Type,
  URI,
  URIs,
  UnavailablePlaces,
  UpdateSeatingReservationRequest,
  UpdateSeatingReservationResponse,
  VerticalSequencing,
  WebhookIdentityType,
  utils,
} from './seating-v1-seating-reservation-seating-reservation.universal.js';
